import { useQuery } from '@tanstack/react-query'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { RequestResponse, RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'

export const getRequestOperationalStatusQueryKey = (requestId: string) => ['requestOperationalStatus', requestId]

export type RequestOperationalStatusDetail = NonNullable<RequestResponse<'/{id}/operational-status', 'get'>['data']>

export const useGetRequestOperationalStatus = ({
  requestId,
  enabled = true,
  initialData,
}: {
  requestId: string
  enabled?: boolean
  initialData?: RequestOperationalStatusDetail | null
}) => {
  const { getToken } = useAuthenticatedUser()

  return useQuery({
    queryKey: getRequestOperationalStatusQueryKey(requestId),
    staleTime: 60_000,
    // The API pushes nothing, so poll to see a run queued by any action, ours or another user's, within the scheduler's minute.
    refetchInterval: 15_000,
    enabled,
    initialData,
    queryFn: async () => {
      const apiClient = new RequestsHttpClient({ authorization: await getToken() })
      const { data, error } = await apiClient.GET('/{id}/operational-status', {
        params: { path: { id: requestId } },
      })
      // 404 = org flag off, request soft-deleted, or no status generated yet — all "nothing to show".
      if (error?.type === 'NotFoundError') return null
      throwIfApiError(error)
      return data ?? null
    },
  })
}
